import React, { useRef, useEffect, useMemo } from "react";
import { Sprite, Graphics, Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import anime from "animejs";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { openGameRules } from "../../../../State/actions";
import { selectMenuActive } from "../../../../State/selectors";
import { SOUND_NAME } from "../../../../sounds";
import { getNewUITexture } from "../../../../assets";
import ButtonShadow from "./ButtonShadow";
import { PixiSettings } from "../../../../pixiSettings";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../pixiSettings";
import { useSecondaryToolbar } from "../../../../Hooks/useSecondaryToolbar";
import { soundManager } from "../../../../SoundManager";
import { useButtonBrightness } from "../../../../Hooks/useButtonBrightness";

const ButtonGameRules = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getResponsiveConfig, device, orientation, dimensions, isMobileSmall } =
    useDimensions();
  const toolbar = useSecondaryToolbar();
  const menuActive = useSelector(selectMenuActive);
  const animationRef = useRef(null);

  // Refs to track current dimensions for animation (no re-renders)
  const dimensionsRef = useRef({ width: 0, height: 0 });
  const isInitializedRef = useRef(false);

  // Track previous menuActive state to detect ACTUAL changes (not just re-renders)
  const previousMenuActiveRef = useRef(menuActive);

  // Shared hover/press hook
  const { buttonRef, handlePointerOver, handlePointerOut, triggerPress, cleanupFilters } =
    useButtonBrightness();


  const texture = getNewUITexture("icon_rules");

  // Override shared config — match menu button size on mobile portrait
  const MENU_BUTTON_CONFIG = useMemo(() => ({
    ...USER_INTERFACE_BUTTON_CONFIG,
    DEVICE_CONFIGS: {
      ...USER_INTERFACE_BUTTON_CONFIG.DEVICE_CONFIGS,
      [PixiSettings.DEVICE.MOBILE]: {
        ...USER_INTERFACE_BUTTON_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.MOBILE],
        [PixiSettings.ORIENTATION.PORTRAIT]: {
          deviceMultiplier: 2.2,
        },
      },
    },
  }), []);

  // Get responsive configuration from context - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () => getResponsiveConfig(MENU_BUTTON_CONFIG, true),
    [getResponsiveConfig, MENU_BUTTON_CONFIG]
  );

  // Calculate responsive button position based on device and orientation - memoized
  // Position is relative to menu button with consistent gap (1st button from menu)
  const buttonPosition = useMemo(() => {
    const gap = buttonConfig.height * 1.2;

    if (isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        const menuY = buttonConfig.height * 1.3;
        return {
          x: buttonConfig.width * 0.925,
          y: menuY + gap
        };
      } else {
        const menuY = dimensions.height - toolbar.height / 2;
        return {
          x: buttonConfig.width * 0.6,
          y: menuY - gap
        };
      }
    }
    if (device === PixiSettings.DEVICE.MOBILE) {
      // Menu button Y position (must match ButtonMenu.jsx)
      const menuY = orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? buttonConfig.height * 1.6
        : dimensions.height - toolbar.height / 2;
      return {
        x: orientation === PixiSettings.ORIENTATION.PORTRAIT
          ? buttonConfig.width * 0.85
          : buttonConfig.width * 0.6,
        y: orientation === PixiSettings.ORIENTATION.PORTRAIT
          ? menuY + gap
          : menuY - gap
      };
    } else if (device === PixiSettings.DEVICE.TABLET) {
      // Menu button Y position (must match ButtonMenu.jsx)
      const menuY = buttonConfig.height * 0.8;
      return {
        x: buttonConfig.width * 0.8,
        y: menuY + gap
      };
    } else {
      // Desktop: position relative to menu button (bottom of toolbar)
      const menuY = dimensions.height - toolbar.height / 2;
      return {
        x: buttonConfig.width,
        y: menuY - gap
      };
    }
  }, [device, orientation, dimensions.height, buttonConfig.width, buttonConfig.height, toolbar.height, isMobileSmall]);

  const handleClick = () => {
    soundManager.play(SOUND_NAME.button_click);

    // Add press animation before opening game rules
    triggerPress({
        targetWidth: buttonConfig.width,
        targetHeight: buttonConfig.height
      });

    dispatch(openGameRules());
  };

  // Initialize sprite dimensions on mount ONLY - using sprite's own flag to prevent re-initialization
  useEffect(() => {
    if (buttonRef.current && !buttonRef.current._initialized) {
      buttonRef.current.width = 0;
      buttonRef.current.height = 0;
      dimensionsRef.current.width = 0;
      dimensionsRef.current.height = 0;
      buttonRef.current._originalWidth = buttonConfig.width;
      buttonRef.current._originalHeight = buttonConfig.height;
      buttonRef.current._initialized = true;
      isInitializedRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonConfig.width, buttonConfig.height]);

  // Update stored dimensions on resize (no animation)
  useEffect(() => {
    if (!buttonRef.current || !isInitializedRef.current) return;

    const buttonSprite = buttonRef.current;

    if (buttonSprite._originalWidth !== buttonConfig.width || buttonSprite._originalHeight !== buttonConfig.height) {
      buttonSprite._originalWidth = buttonConfig.width;
      buttonSprite._originalHeight = buttonConfig.height;

      // If menu is open, update sprite size immediately without animation
      if (menuActive && buttonSprite.width > 0) {
        buttonSprite.width = buttonConfig.width;
        buttonSprite.height = buttonConfig.height;
        dimensionsRef.current.width = buttonConfig.width;
        dimensionsRef.current.height = buttonConfig.height;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonConfig.width, buttonConfig.height, menuActive]);

  // Width and height animation effect - ONLY triggers when menuActive ACTUALLY CHANGES
  useEffect(() => {
    if (!buttonRef.current || !isInitializedRef.current) return;

    // Check if menuActive actually changed (not just a re-render)
    if (previousMenuActiveRef.current === menuActive) return;
    previousMenuActiveRef.current = menuActive;

    const buttonSprite = buttonRef.current;

    // Stop any existing animation
    if (animationRef.current) {
      anime.remove(animationRef.current);
    }

    // Get current dimensions from sprite (preserve current state)
    const currentWidth = buttonSprite.width || 0;
    const currentHeight = buttonSprite.height || 0;

    // Update ref with current values
    dimensionsRef.current.width = currentWidth;
    dimensionsRef.current.height = currentHeight;

    if (menuActive) {
      // Animate from current size to target width and height (first to appear - no delay)
      animationRef.current = anime({
        targets: dimensionsRef.current,
        width: buttonSprite._originalWidth,
        height: buttonSprite._originalHeight,
        duration: 200,
        delay: 0,
        easing: "easeOutQuad",
        update: () => {
          if (buttonSprite) {
            buttonSprite.width = dimensionsRef.current.width;
            buttonSprite.height = dimensionsRef.current.height;
          }
        }
      });
    } else {
      // Animate from current size back to 0 (last to close - 200ms delay)
      animationRef.current = anime({
        targets: dimensionsRef.current,
        width: 0,
        height: 0,
        duration: 200,
        delay: 200,
        easing: "easeInQuad",
        update: () => {
          if (buttonSprite) {
            buttonSprite.width = dimensionsRef.current.width;
            buttonSprite.height = dimensionsRef.current.height;
          }
        }
      });
    }

    // Cleanup function to stop animation on unmount
    return () => {
      if (animationRef.current) {
        anime.remove(animationRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuActive]);

  useEffect(() => () => cleanupFilters(), [cleanupFilters]);

  // Calculate button radius to match the visual sprite exactly - memoized
  const buttonRadius = useMemo(
    () => buttonConfig.width / 2,
    [buttonConfig.width]
  );

  return (
    <Container
      name="secondary_button_game_rules"
      x={buttonPosition.x}
      y={buttonPosition.y}
      zIndex={2}
    >
      {/* Interactive circular area - handles all pointer events */}
      <Graphics
        name="button_game_rules_interactive_area"
        eventMode={menuActive ? "static" : "none"}
        cursor="pointer"
        hitArea={new PIXI.Circle(0, 0, buttonRadius)}
        onpointertap={handleClick}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        draw={(g) => {
          g.clear();
          // Nearly invisible fill (required for hit detection to work)
          g.beginFill(0x000000, 0.01);
          g.drawCircle(0, 0, buttonRadius);
          g.endFill();
        }}
      />

      {/* Visual sprite (non-interactive, just for display) */}
      {/* Note: width/height are controlled by animation, not props */}
      {/* Soft drop shadow under it (ButtonShadow, utils/buttonShadow) */}
      <ButtonShadow target={buttonRef} texture={texture} width={buttonConfig.width} height={buttonConfig.height} />
      <Sprite
        ref={buttonRef}
        texture={texture}
        name="button_game_rules_icon"
        anchor={0.5}
        x={0}
        y={0}
        eventMode="none"
        accessibleTitle={t("game_rules")}
        accessibleHint={t("game_rules")}
        roundPixels={true}
      />
    </Container>
  );
};

export default ButtonGameRules;
